/**
 * Creator theme — shared constants.
 *
 * The theme is expressed as a `data-theme` attribute on `<html>`, NOT as a
 * class, and NOT as React state. Three consequences follow, and all three are
 * load-bearing:
 *
 *   1. **Portals inherit it.** Radix `Dialog`/`Popover`/`AlertDialog` portal to
 *      `document.body`, which is under `<html>` — so a portaled surface picks up
 *      the creator's theme with no per-surface opt-in. This is what retires the
 *      `contentClassName="dark"` workaround.
 *   2. **It is stamped before first paint** by `ThemeScript`, so there is no
 *      flash. Anything that reads the theme during render would have to wait for
 *      hydration; anything that reads it in CSS does not.
 *   3. **A subtree can pin its own value** (`ThemeScope`) without touching the
 *      document. That is how the end-user surfaces (bio, checkout, PDP,
 *      `(eux)`) stay on the `visitor` palette regardless of what the signed-in
 *      creator picked in another tab.
 *
 * `visitor` is a real third value, not an absence: `:root` carries the legacy
 * shadcn light palette that every public surface renders with today, and
 * `[data-theme='visitor']` re-declares exactly those values so an inherited
 * creator theme can be overridden back to it.
 */
export const CREATOR_THEMES = ['dark', 'light'] as const;

export type CreatorTheme = (typeof CREATOR_THEMES)[number];

/** Every value the `data-theme` attribute may legitimately hold. */
export type ThemeScopeValue = CreatorTheme | 'visitor';

/**
 * `localStorage` key holding the creator's choice. Read by `ThemeScript`
 * (inlined into the document, so this string is duplicated there as a literal —
 * keep the two in sync) and written by `useCreatorTheme().setTheme`.
 */
export const CREATOR_THEME_STORAGE_KEY = 'ps-creator-theme';

/** The attribute the theme is expressed as, on `<html>` and on any scope. */
export const CREATOR_THEME_ATTRIBUTE = 'data-theme';

/**
 * What a creator who has never touched the toggle sees. Light: it started as
 * dark while the creator surfaces were still hardcoded dark (so the token
 * migration was a no-op until someone opted in); with light mode landed, light
 * is the product default. A stored choice always wins over this.
 */
export const DEFAULT_CREATOR_THEME: CreatorTheme = 'light';

export function isCreatorTheme(value: unknown): value is CreatorTheme {
  return value === 'dark' || value === 'light';
}

/** Narrows a raw `data-theme` attribute read off the DOM. */
export function isThemeScopeValue(value: unknown): value is ThemeScopeValue {
  return isCreatorTheme(value) || value === 'visitor';
}
